import React, { useCallback, useEffect, useMemo, useState } from "react";
import api, { formatApiError, formatTanggal } from "@/lib/api";
import Layout, { PageTitle } from "@/components/Layout";
import { NAV_KEPSEK } from "@/lib/nav";
import { FilterBar } from "@/components/FilterBar";
import BackupExcelButton from "@/components/BackupExcelButton";
import { toast } from "sonner";
import { Pencil, Trash2, X } from "lucide-react";

const MAPEL_AKADEMIK = ["Matematika", "Pendidikan Pancasila", "PAI", "Bahasa Inggris", "IPAS", "Bahasa Indonesia", "Calistung", "PJOK", "IPA", "IPS"];
const MAPEL_NON = ["Seni Musik", "Seni Tari", "Seni Lukis", "Tata Boga", "TIK", "Desain Grafis", "Prakarya", "Pengembangan Diri", "OMSK", "Komunikasi", "Pramuka", "Boccee", "Athletik"];
const MAPEL_EKSTRA = ["Bahasa Reseptif", "Bahasa Ekspresif", "Preakademik", "Kemampuan Imitasi", "Bina Diri", "Kemampuan Wicara", "Calistung"];

const F_EMPTY = { siswa: "", jenis: "", tahun_ajaran: "", semester: "", mapel: "" };

export default function BukuBatasKepsek() {
  const [all, setAll] = useState([]);
  const [filters, setFilters] = useState(F_EMPTY);
  const [applied, setApplied] = useState(F_EMPTY);
  const [editItem, setEditItem] = useState(null);

  const load = useCallback(async () => {
    try { const { data } = await api.get("/buku-batas"); setAll(data); } catch (e) { console.error(e); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const items = all.filter((b) => {
    if (applied.siswa && !(b.nama_siswa || "").toLowerCase().includes(applied.siswa.toLowerCase())) return false;
    if (applied.jenis && b.jenis !== applied.jenis) return false;
    if (applied.tahun_ajaran && b.tahun_ajaran !== applied.tahun_ajaran) return false;
    if (applied.semester && b.semester !== applied.semester) return false;
    if (applied.mapel && !b.mata_pelajaran.toLowerCase().includes(applied.mapel.toLowerCase())) return false;
    return true;
  });

  const mapelList = useMemo(() => (
    editItem?.jenis === "akademik" ? MAPEL_AKADEMIK : editItem?.jenis === "non_akademik" ? MAPEL_NON : MAPEL_EKSTRA
  ), [editItem?.jenis]);

  const del = async (it) => {
    if (!window.confirm(`Hapus buku batas ${it.nama_siswa} - ${it.mata_pelajaran}?`)) return;
    try { await api.delete(`/buku-batas/${it.id}`); toast.success("Dihapus"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  const saveEdit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        siswa_id: editItem.siswa_id,
        tahun_ajaran: editItem.tahun_ajaran,
        semester: editItem.semester,
        jenis: editItem.jenis,
        mata_pelajaran: editItem.mata_pelajaran,
        materi: editItem.materi,
        sub_materi: editItem.sub_materi || "",
        tugas_pr: editItem.tugas_pr || "",
        catatan: editItem.catatan || "",
      };
      await api.put(`/buku-batas/${editItem.id}`, payload);
      toast.success("Perubahan tersimpan");
      setEditItem(null);
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
  };

  return (
    <Layout nav={NAV_KEPSEK}>
      <PageTitle title="Buku Batas (Semua)" action={<BackupExcelButton endpoint="buku-batas" filters={{ tahun_ajaran: applied.tahun_ajaran, semester: applied.semester, jenis: applied.jenis }} testId="backup-buku-batas-btn" />} />
      <FilterBar
        filters={filters} setFilters={setFilters}
        onApply={() => setApplied({ ...filters })}
        onReset={() => { setFilters(F_EMPTY); setApplied(F_EMPTY); }}
        dataTestidPrefix="bbatas-filter"
        fields={[
          { key: "siswa", type: "text", label: "Siswa", placeholder: "Cari siswa" },
          { key: "jenis", type: "select", label: "Jenis", options: [{ v: "akademik", label: "Akademik" }, { v: "non_akademik", label: "Non-Akademik" }, { v: "ekstra", label: "Ekstra" }] },
          { key: "tahun_ajaran", type: "select", label: "TA", options: ["2023/2024", "2024/2025", "2025/2026", "2026/2027"] },
          { key: "semester", type: "select", label: "Sem", options: [{ v: "I", label: "I" }, { v: "II", label: "II" }] },
          { key: "mapel", type: "text", label: "Mapel", placeholder: "Cari mapel" },
        ]}
      />

      {editItem && (
        <section className="pp-card p-6 mb-6 animate-fade-up" data-testid="bbatas-edit-form">
          <div className="flex items-center justify-between mb-5">
            <h3 className="text-lg font-semibold">Edit Buku Batas — {editItem.nama_siswa}</h3>
            <button onClick={() => setEditItem(null)} data-testid="bbatas-edit-close"><X className="w-5 h-5 text-slate-400" /></button>
          </div>
          <form onSubmit={saveEdit} className="grid sm:grid-cols-2 gap-4">
            <div><label className="pp-label">Tahun Ajaran</label>
              <select className="pp-input" value={editItem.tahun_ajaran} onChange={(e) => setEditItem({ ...editItem, tahun_ajaran: e.target.value })}>
                <option>2023/2024</option><option>2024/2025</option><option>2025/2026</option><option>2026/2027</option>
              </select></div>
            <div><label className="pp-label">Semester</label>
              <select className="pp-input" value={editItem.semester} onChange={(e) => setEditItem({ ...editItem, semester: e.target.value })}>
                <option value="I">Semester I</option><option value="II">Semester II</option>
              </select></div>
            <div><label className="pp-label">Jenis</label>
              <select className="pp-input" value={editItem.jenis} onChange={(e) => {
                const j = e.target.value;
                const list = j === "akademik" ? MAPEL_AKADEMIK : j === "non_akademik" ? MAPEL_NON : MAPEL_EKSTRA;
                setEditItem({ ...editItem, jenis: j, mata_pelajaran: list.includes(editItem.mata_pelajaran) ? editItem.mata_pelajaran : list[0] });
              }}>
                <option value="akademik">Akademik</option><option value="non_akademik">Non Akademik</option><option value="ekstra">Ekstra</option>
              </select></div>
            <div><label className="pp-label">Mata Pelajaran</label>
              <select className="pp-input" value={editItem.mata_pelajaran} onChange={(e) => setEditItem({ ...editItem, mata_pelajaran: e.target.value })}>
                {mapelList.map(m => <option key={m}>{m}</option>)}
              </select></div>
            <div><label className="pp-label">Materi</label>
              <input required className="pp-input" value={editItem.materi} onChange={(e) => setEditItem({ ...editItem, materi: e.target.value })} data-testid="bbatas-edit-materi" /></div>
            <div><label className="pp-label">Sub Materi</label>
              <input className="pp-input" value={editItem.sub_materi || ""} onChange={(e) => setEditItem({ ...editItem, sub_materi: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="pp-label">Tugas / PR</label>
              <textarea rows={2} className="pp-input" value={editItem.tugas_pr || ""} onChange={(e) => setEditItem({ ...editItem, tugas_pr: e.target.value })} /></div>
            <div className="sm:col-span-2"><label className="pp-label">Catatan</label>
              <textarea rows={2} className="pp-input" value={editItem.catatan || ""} onChange={(e) => setEditItem({ ...editItem, catatan: e.target.value })} /></div>
            <div className="sm:col-span-2 flex justify-end gap-2">
              <button type="button" onClick={() => setEditItem(null)} className="pp-btn-secondary">Batal</button>
              <button type="submit" className="pp-btn-primary" data-testid="bbatas-edit-save">Simpan Perubahan</button>
            </div>
          </form>
        </section>
      )}

      <section className="pp-card pp-freeze overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-[1100px] w-full text-sm">
            <thead><tr className="pp-table-header">
              <th className="text-left px-5 py-3">Tanggal</th>
              <th className="text-left px-5 py-3">Siswa</th>
              <th className="text-left px-5 py-3">Guru Pendamping</th>
              <th className="text-left px-5 py-3">Jenis</th>
              <th className="text-left px-5 py-3">Mapel</th>
              <th className="text-left px-5 py-3">Materi</th>
              <th className="text-left px-5 py-3">Catatan</th>
              <th className="text-right px-5 py-3">Aksi</th>
            </tr></thead>
            <tbody>
              {items.length === 0 && <tr><td colSpan={8} className="text-center py-10 text-slate-400 italic">Belum ada data.</td></tr>}
              {items.map((it) => (
                <tr key={it.id} className="pp-row">
                  <td className="px-5 py-3 text-xs">{formatTanggal(it.created_at)}</td>
                  <td className="px-5 py-3 font-medium">{it.nama_siswa}</td>
                  <td className="px-5 py-3 text-slate-700">{it.nama_guru || "—"}</td>
                  <td className="px-5 py-3 capitalize">{it.jenis.replace("_", " ")}</td>
                  <td className="px-5 py-3">{it.mata_pelajaran}</td>
                  <td className="px-5 py-3 text-slate-700">{it.materi} {it.sub_materi && <span className="text-slate-400 italic">/ {it.sub_materi}</span>}</td>
                  <td className="px-5 py-3 text-xs text-slate-600">{it.catatan || "—"}</td>
                  <td className="px-5 py-3 text-right">
                    <div className="flex justify-end gap-2">
                      <button onClick={() => setEditItem({ ...it })} className="pp-btn-secondary" data-testid={`bbatas-edit-${it.id}`}>
                        <Pencil className="w-3.5 h-3.5" />Edit
                      </button>
                      <button onClick={() => del(it)} className="pp-btn-danger" data-testid={`bbatas-delete-${it.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />Hapus
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>
    </Layout>
  );
}
